import { AddToCartButtonType } from '@commerce-cart-webcomponents/ui-composite';
import type { ProductData, Slot } from '@commerce-product-webcomponents/sdk';
import { ButtonSize } from '@nextgen-web-framework/all';
import { FC } from 'react';
import { ProductLink } from '../../Hoc';
import {
  BasicDetailsComponent,
  ConditionalButtonComponent,
  ConditionalPriceRangeComponent,
  ConditionalRetailPriceRangeComponent,
  DerivedAvailabilityStatusComponentGroupKey,
  DetailsType,
  ImageComponent,
  ImageType,
  PVBVComponent,
  PVBVType,
  StockStatusComponent,
  StockStatusComponentType,
  includesDerivedAvailabilityStockStatus,
} from '../../Molecules';
import { PriceType } from '../../Molecules/PriceComponent/types/enums';
import type { BundleSlotEntriesType, BundleSlotType } from '../../SearchCard/SearchCard';
import styles from './ResultCardComponent.module.scss';
import type { ResultCardComponentPropsTypes } from './types/propsTypes';

const SearchHighlighter = ({
  keyword,
  content,
  className = '',
}: {
  keyword: string;
  content: string;
  className?: string;
}): JSX.Element => {
  let newContent = '',
    remainingContent = content;
  const matches = content.match(new RegExp(keyword.replace(/[^\w\s]/gi, ''), 'gi')) || [];

  for (const match of matches) {
    const newString = remainingContent.slice(0, remainingContent.indexOf(match) + match.length);
    remainingContent = remainingContent.slice(remainingContent.indexOf(match) + match.length);
    newContent += newString.replace(match, `<span class=${styles['searched-keyword-hilighter']}>${match}</span>`);
  }

  newContent += remainingContent;

  return (
    <div className={styles[className]} data-testid="search-content">
      <span dangerouslySetInnerHTML={{ __html: newContent }} />
    </div>
  );
};

export const ResultCardComponent: FC<ResultCardComponentPropsTypes> = ({
  disabled,
  entryObj,
  searchedKeyword,
  isABOCard = false,
  showComponentCollection = {
    showAddToCartButtonComp: true,
    showPVBVComp: true,
    showStockStatusComp: true,
  },
  stockStatusType = StockStatusComponentType.STOCK_STATUS_ABOCHECKBOX,
  showSubText = false,
  showPrefix,
  dateSubText,
  onProductClick,
  onClick,
  className,
  size = ButtonSize.SMALL,
  productData,
  addToCartButtonType = AddToCartButtonType.ADD_TO_CART_BUTTON,
  showSubscriptionSelectButtonComp = false,
  showLink = true,
  isMinProjection = true,
  showZeroAsFree = false,
}: ResultCardComponentPropsTypes): JSX.Element => {
  const keyword = searchedKeyword.trim();
  const showProdLink = !isABOCard && !productData?.productFlags?.restrictFromPdpDisplay;
  const derivedBundleFlag = productData?.isBundle;
  const derivedProductId = productData?.productId || '';
  const derivedProductName = productData?.productName ?? '';
  const derivedBrandName = productData?.brandName || '';

  const productLinkProps = {
    productId: derivedProductId,
    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => onProductClick?.(e, derivedProductId),
    showLink: showProdLink,
    href: '',
    productName: derivedProductName,
  };

  const bundleState = productData?.bundle;
  const bundleSlotsState: BundleSlotType[] = bundleState?.slots?.map((slot: Slot) => {
    const bundleSlot: BundleSlotType = {
      id: slot.id?.toString() as string,
      entries: slot.products?.map((product: ProductData) => {
        const bundleSlotEntry = {
          product: {
            id: product.items[0].itemNumber as string,
          },
          quantity: slot.minQty,
        };
        return bundleSlotEntry;
      }) as unknown as BundleSlotEntriesType[],
    };
    return bundleSlot;
  }) as BundleSlotType[];

  const renderStockStatusComp = () => (
    <StockStatusComponent
      derivedAvailabilityStatus={productData?.derivedAvailabilityStatus}
      showSubText={showSubText}
      stockStatusType={stockStatusType}
      dateSubText={dateSubText}
      firstAvailableFutureDate={productData?.firstAvailableFutureDate}
      showPrefix={showPrefix}
    />
  );

  const renderStockStatusAsSearchResultCard = (): boolean =>
    Boolean(
      showComponentCollection.showStockStatusComp &&
        productData?.derivedAvailabilityStatus &&
        stockStatusType === StockStatusComponentType.STOCK_STATUS_SEARCH_RESULT_CARD,
    );

  const renderStockStatusAsABOCheckbox = (): boolean =>
    Boolean(
      showComponentCollection.showStockStatusComp &&
        productData?.derivedAvailabilityStatus &&
        stockStatusType === StockStatusComponentType.STOCK_STATUS_ABOCHECKBOX,
    );

  const isBtnDisabled = () =>
    disabled ||
    productData?.productFlags?.restrictFromAddToCart ||
    includesDerivedAvailabilityStockStatus(
      productData?.derivedAvailabilityStatus || '',
      DerivedAvailabilityStatusComponentGroupKey.CTA_DISABLED_STATUSES,
    );

  return (
    <>
      <div data-testid="search-container-test-id" className={`${styles['search-result-card-container']} ${className}`}>
        <div className={styles['card-image']}>
          <ProductLink className={styles['result-card-product-link']} {...productLinkProps}>
            <ImageComponent
              link={productData?.mainLink ?? ''}
              alt={productData?.alt || ''}
              imagetype={ImageType.SEARCH_RESULT_CARD}
            />
          </ProductLink>
        </div>
        <div className={styles['card-details']}>
          <div className={styles['card-description']}>
            <ProductLink {...productLinkProps}>
              {keyword ? (
                <SearchHighlighter keyword={keyword} content={[derivedBrandName, productData?.productName].join(' ')} />
              ) : (
                <BasicDetailsComponent
                  brandName={derivedBrandName}
                  productName={productData?.productName || ''}
                  detailsType={isABOCard ? DetailsType.DETAILSABOCHECKBOXCARD : DetailsType.DETAILSSEARCHRESULTCARD}
                />
              )}
            </ProductLink>
            <div className={styles['prices-details']}>
              <ConditionalPriceRangeComponent
                isBundle={!!derivedBundleFlag}
                minPrice={productData?.minPrice || 0}
                maxPrice={productData?.maxPrice || 0}
                currency={productData?.currency || ''}
                productPrice={productData?.productPrice || 0}
                condionalPriceType={isABOCard ? PriceType.PRICEABOCHECKBOXCARD : PriceType.PRICESEARCHRESULTCARD}
                showZeroAsFree={showZeroAsFree}
              />
              <ConditionalRetailPriceRangeComponent
                isBundle={!!derivedBundleFlag}
                minPrice={productData?.minRetailPrice as number}
                maxPrice={productData?.maxRetailPrice as number}
                currency={productData?.currency || ''}
                productPrice={productData?.productRetailPrice as number}
                condionalPriceType={isABOCard ? PriceType.PRICEABOCHECKBOXCARD : PriceType.PRICESEARCHRESULTCARD}
                isRetailPrice={true}
              />
              {showComponentCollection?.showPVBVComp && (
                <PVBVComponent
                  pointValue={productData?.pointValue || ''}
                  businessVolume={productData?.businessVolume || ''}
                  pvbvType={PVBVType.PVBVSEARCHCARD}
                />
              )}
            </div>
          </div>
          {renderStockStatusAsSearchResultCard() && renderStockStatusComp()}
          {showComponentCollection?.showAddToCartButtonComp && (
            <ConditionalButtonComponent
              derivedAvailabilityStatus={productData?.derivedAvailabilityStatus}
              showLink={showLink}
              disabled={isBtnDisabled()}
              entryObj={entryObj}
              bundleSlots={bundleSlotsState}
              className={styles['cpw-search-result-conditional-button']}
              size={size}
              onClick={onClick}
              productData={productData}
              addToCartButtonType={addToCartButtonType}
              showSubscriptionSelectButtonComp={showSubscriptionSelectButtonComp}
              isMinProjection={isMinProjection}
            />
          )}
          {renderStockStatusAsABOCheckbox() && renderStockStatusComp()}
        </div>
      </div>
    </>
  );
};
export default ResultCardComponent;
export type { ResultCardComponentPropsTypes };
